import * as $ from 'jquery';
import ReactDOM from 'react-dom';
import { ApplicationController } from '@aha-app/mvc';
import { AiUserAgent, AiUserAgentFavorite } from 'components/framework/models';
import { type AiAgentTemplateResponse } from './types';
import { some } from 'lodash';
import { generateKeyBetween } from 'fractional-indexing';
import AhaAnalytics from 'javascripts/analytics';

// NOTE: Extend jQuery type to include rails UJS methods
declare global {
  interface JQueryStatic {
    rails: {
      ajax: (options: any) => void;
    };
  }
}

export const agentTypeNames = {
  PROMPT: 'request',
  INSTRUCTION: 'instruction',
  AGENT: 'agent',
};

const DEFAULT_NEW_PATH = '/settings/ai_agents/new?tab=';

/* eslint-disable no-unused-vars */
export enum TemplateCategories {
  All = 'All',
  Workspace = 'Workspace',
  Personal = 'Personal',
  Favorites = 'Favorites',
  Global_All = 'Global All',
  General = 'General',
  Strategy = 'Strategy',
  Research = 'Research',
  Discovery = 'Discovery',
  Ideation = 'Ideation',
  Roadmaps = 'Roadmaps',
  Plans = 'Plans',
  Designs = 'Designs',
  Presentations = 'Presentations',
  Development = 'Development',
  Documentation = 'Documentation',
  Launches = 'Launches',
  Analysis = 'Analysis',
}

/* eslint-disable no-unused-vars */
export enum TemplateTypes {
  ALL = 'ALL',
  PROMPT = 'PROMPT',
  INSTRUCTION = 'INSTRUCTION',
  AGENT = 'AGENT',
}
/* eslint-enable no-unused-vars */
export type TemplateType = keyof typeof TemplateTypes;

export type TemplateCategory = keyof typeof TemplateCategories;

export type TemplateControllerState = {
  // Available templates.
  templates: {
    workspace: AiUserAgent[];
    personal: AiUserAgent[];
    global: AiUserAgent[];
  };
  // User's favorite agents
  favoriteAgents: AiUserAgentFavorite[];
  // Loading the templates list
  loadingTemplates: boolean;
  // Whether controller is loading (for e.g., templates or placing a template)
  loading: boolean;
  // Currently selected category.
  currentCategory?: TemplateCategories;
  // Currently selected template for preview.
  currentTemplate?: AiUserAgent;
  // If preview page should be fullscreened
  fullScreen: boolean;
  // Template type (all, agent, instruction, prompt)
  templateType: TemplateType;
  // Override function to place template manually after selection
  placeTemplate?: (template: AiUserAgent) => void | Promise<void>;
  // Override function to close template modal
  closeTemplateModal?: () => void;
  // Javascript function to call when the modal is closing
  afterView?: string;
};

export const createEmptyState = (): TemplateControllerState => ({
  templates: {
    workspace: [],
    personal: [],
    global: [],
  },
  favoriteAgents: [],
  loadingTemplates: true,
  loading: true,
  fullScreen: false,
  templateType: TemplateTypes.ALL,
});

export type TemplateControllerProps = {
  templateType?: TemplateType;
  placeTemplate?: (template: AiUserAgent) => void | Promise<void>;
  closeTemplateModal?: () => void;
  newPath?: string;
  hideMyInputs?: boolean;
  category?: TemplateCategory;
  templateId?: string;
};

class AiTemplatesController extends ApplicationController<
  TemplateControllerState,
  TemplateControllerProps
> {
  static initialState = createEmptyState();

  // List of available categories from Aha! global templates.
  categories: TemplateCategories[] = [
    TemplateCategories.Global_All,
    TemplateCategories.Strategy,
    TemplateCategories.Research,
    TemplateCategories.Discovery,
    TemplateCategories.Ideation,
    TemplateCategories.Roadmaps,
    TemplateCategories.Plans,
    TemplateCategories.Designs,
    TemplateCategories.Presentations,
    TemplateCategories.Development,
    TemplateCategories.Documentation,
    TemplateCategories.Launches,
    TemplateCategories.Analysis,
  ];

  planMap = {
    wess: window.currentAccount?.whiteboardsFlavor,
    wadv: window.currentAccount?.whiteboardsAdvanced,
    kess:
      window.currentAccount?.knowledgeFlavor ||
      window.currentAccount?.ideasFlavor,
    kadv: window.currentAccount?.knowledgeAdvanced,
    dev: window.currentAccount?.teamworkOrDevelopFlavor,
  };

  async initialize({
    templateType,
    placeTemplate,
    closeTemplateModal,
    hideMyInputs,
    category,
    templateId,
  }: TemplateControllerProps) {
    this.state.currentCategory =
      category || hideMyInputs
        ? TemplateCategories.Global_All
        : TemplateCategories.All;

    this.state.templateType = templateType || TemplateTypes.ALL;

    await Promise.all([this.loadTemplates(), this.loadFavorites()]);
    this.state.loading = false;
    // Set hooks for standalone whiteboards to interact with template modal
    this.state.placeTemplate = placeTemplate;
    this.state.closeTemplateModal = closeTemplateModal;

    if (templateId) {
      const template = this.state.templates.global.find(
        t => t.slug === templateId
      );
      if (template) {
        this.actionSetCurrentTemplate(template);
      }
    }
  }

  async loadFavorites() {
    const favorites = await AiUserAgentFavorite.select('id', 'position')
      .merge({
        aiUserAgent: ['id'],
      })
      .all();
    this.state.favoriteAgents = favorites.sort((a, b) => {
      return (a.position || '') < (b.position || '') ? -1 : 1;
    });
  }

  async loadTemplates() {
    this.state.loadingTemplates = true;

    const projectAndPersonalAgents = await AiUserAgent.select(
      'id',
      'name',
      'agentType',
      'capabilities',
      'categories',
      'plans',
      'agentScope',
      'createdAt',
      'markdownInstruction',
      'thumbnail'
    )
      .merge({
        shortDescription: ['htmlBody'],
        longDescription: ['htmlBody'],
        description: ['htmlBody'],
        guideDescription: ['htmlBody'],
      })
      .where({
        projectId: window.currentProject?.id,
      })
      .all();

    const workspaceTemplates = projectAndPersonalAgents.filter(
      template => template.agentScope === 'WORKSPACE'
    );
    const personalTemplates = projectAndPersonalAgents.filter(
      template => template.agentScope === 'PERSONAL'
    );

    const result = (await $.post(this.globalTemplatesUrl, {
      query: `{
        aiUserAgents {
          id
          name
          agentType
          capabilities
          categories
          plans
          agentScope
          description
          shortDescription
          longDescription
          createdAt
          markdownInstruction
          guideDescription
          thumbnail
          slug
        }
      }`,
    }).catch(error => {
      console.error('Error fetching global templates');
      console.error(JSON.stringify(error));
      throw new Error('Failed to load templates');
    })) as { data: { aiUserAgents: { nodes: AiAgentTemplateResponse[] } } };

    // Normalize global templates to match graphql api
    // @ts-ignore
    const globalTemplates = result.data.aiUserAgents.nodes
      .filter(template => {
        return (
          // Fail open if no plans are set/we get null/undefined
          !template.plans ||
          // Roadmaps can always see all templates
          window?.currentAccount?.roadmapFlavor ||
          some(template.plans.map(plan => this.planMap[plan]))
        );
      })
      .map(template => {
        return {
          ...template,
        } as AiAgentTemplateResponse;
      });

    this.state.templates = {
      workspace: workspaceTemplates,
      personal: personalTemplates,
      // @ts-ignore
      global: globalTemplates,
    };
    this.state.loadingTemplates = false;
  }

  actionSetCategory(category) {
    this.state.currentCategory = category;
  }

  actionSetTemplateType(templateType: TemplateType) {
    this.state.templateType = templateType;
  }

  actionSetCurrentTemplate(template: AiUserAgent | undefined) {
    this.state.currentTemplate = template;
  }

  actionPlaceTemplate(template: AiUserAgent, location: string, from: string) {
    AhaAnalytics.track(
      'Feature-7530436202602257500',
      'User selects library prompt',
      {
        Location: location,
        'AI prompt type': agentTypeNames[template.agentType].capitalize(),
        'AI library navigation': from,
        'Template category': template.categories?.join(', '),
        'Prompt name': template.name,
      }
    );

    if (this.state.placeTemplate) {
      this.actionAbortChooseTemplate();
      void this.state.placeTemplate(template);
    } else {
      this.actionAbortChooseTemplate();
      // If we have a new path, redirect to it
      $.rails.ajax({
        type: 'GET',
        url: `${
          this.props.newPath || DEFAULT_NEW_PATH
        }${template.agentType.toLowerCase()}s&name=${encodeURIComponent(
          template.name || ''
        )}&prompt=${encodeURIComponent(template.description.htmlBody)}
        &capabilities=${encodeURIComponent(
          template.capabilities?.join(',') || ''
        )}${!this.props.newPath ? `&after_view=prompt_panel` : ''}`,
        dataType: 'script',
      });
    }
  }

  async actionToggleFavorite(template: AiUserAgent) {
    const favoriteIndex = this.state.favoriteAgents.findIndex(
      fav => fav.aiUserAgent.id === template.id
    );

    if (favoriteIndex > -1) {
      // Unfavorite
      void this.state.favoriteAgents[favoriteIndex].destroy();
      this.state.favoriteAgents.splice(favoriteIndex, 1);
    } else {
      AhaAnalytics.track(
        'Feature-7530436202602257500',
        'User favorites library prompt',
        {
          'AI prompt type': agentTypeNames[template.agentType].capitalize(),
          'Prompt name': template.name,
        }
      );
      const position = generateKeyBetween(
        this.state.favoriteAgents[this.state.favoriteAgents.length - 1]
          ?.position || null,
        null
      );
      const favorite = new AiUserAgentFavorite({
        position: position,
        aiUserAgent: { id: template.id },
      });
      await favorite.save();

      this.state.favoriteAgents.push(favorite);
    }
  }

  async actionReorderFavorite(favoriteId: string, newPosition: string) {
    const favorite = this.state.favoriteAgents.find(
      fav => fav.id === favoriteId
    );
    if (!favorite) return;

    favorite.position = newPosition;
    await favorite.save();

    // Re-sort the favorites by position
    this.state.favoriteAgents.sort((a, b) => {
      // Note we need to do a case-sensitive comparison here for the
      // fractional indexing to work correctly.
      return (a.position || '') < (b.position || '') ? -1 : 1;
    });
  }

  isFavorite(agentId: string): boolean {
    return this.state.favoriteAgents.some(
      fav => fav.aiUserAgent.id === agentId
    );
  }

  actionAbortChooseTemplate() {
    this.state.currentTemplate = undefined;
    this.state.loading = false;
    if (this.state.closeTemplateModal) {
      return this.state.closeTemplateModal();
    }

    if (this.parent && Reflect.has(this.parent, 'actionHideTemplateModal')) {
      this.parent.actionHideTemplateModal();
    } else {
      const modal = document.querySelector(
        '[data-react-class=AiTemplatesModal]'
      );
      if (modal) {
        ReactDOM.unmountComponentAtNode(modal);
        modal.parentNode && modal.parentNode.removeChild(modal);
      }
    }
  }

  get globalTemplatesUrl() {
    return `${window.Configatron?.secureUrlBase}/global_ai_agent_templates/graphql`;
  }
}

export default AiTemplatesController;
